import Composer, { BaseComposerArgs } from 'cio/components/action-composer/base-glimmer';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import {
  CreateEventComposerSection,
  DataActionComposerSectionType,
  DataActionConditionType,
  DataActionTriggerAttributeDropdownItem,
  extractUsedPaths,
  getConditionTypeFromTopLevelKey,
  getDefaultConditionType,
  getSupportedConditionTypes,
} from 'cio/utils/campaigns/data-action';
import EventBusService, { FOCUS_ACE_EDITOR, INSERT_LIQUID } from 'cio/services/event-bus';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'cio/models/campaign';
import { keyPathToString, stringToPath } from 'cio/utils/objects';
import { isEmpty } from '@ember/utils';
import { Editor } from 'cio/services/editor';
import DataActionRecipientCondition from 'cio/app-models/data-action/recipient-condition';
import CreateEventActionEventName from 'cio/app-models/create-event/event-name';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import { task } from 'ember-concurrency';
import { isNumeric } from 'cio/utils/numbers';
import TemplateModel from 'cio/models/template';
import { getLiquidToInsert } from 'cio/utils/liquid';

export type DataActionBaseComposerArgs = BaseComposerArgs;

function getRowNameFromPath(path: (string | number)[]) {
  let lastValue = path[path.length - 1];
  let name = `${lastValue}`;

  if (path.some((v) => typeof v === 'number') && path.length > 1) {
    // If the last value is an array's index, iterate up the path until a
    // non-index value is found, and use that whole subpath as the name
    if (typeof lastValue === 'number') {
      let pathCopy = [...path];
      let currentValue = pathCopy.pop();
      let namePath: (string | number)[] = [currentValue as number];

      while (typeof currentValue === 'number') {
        currentValue = pathCopy.pop();
        if (currentValue == null) {
          break;
        }
        namePath.unshift(currentValue);
      }

      name = keyPathToString(namePath);
    }
  }

  return name;
}

interface DataActionBaseComposerSignature {
  Args: DataActionBaseComposerArgs;
}

export default abstract class DataActionBaseComposer<
  T extends DataActionBaseComposerSignature = DataActionBaseComposerSignature,
> extends Composer<T> {
  @service eventBus!: EventBusService;

  @tracked recipientCondition?: DataActionRecipientCondition;
  @tracked eventName?: CreateEventActionEventName;
  @tracked bodyRows?: DataActionBodyAttributeRows;

  constructor(owner: unknown, args: T['Args']) {
    super(owner, args);

    this.subscribeToLiquidInsert();
  }

  willDestroy() {
    super.willDestroy();

    this.unsubscribeFromLiquidInsert();
  }

  @action
  subscribeToLiquidInsert() {
    this.eventBus.subscribe(INSERT_LIQUID, this, this.insertSidebarValue);
  }

  @action
  unsubscribeFromLiquidInsert() {
    this.eventBus.unsubscribe(INSERT_LIQUID, this, this.insertSidebarValue);
  }

  @computed('args.editableEntity.template.id')
  get template() {
    return this.args.editableEntity.template as TemplateModel;
  }

  get supportedConditionTypes() {
    return getSupportedConditionTypes(this.args.editableEntity.campaign as CampaignModel);
  }

  get defaultConditionType() {
    return getDefaultConditionType(this.args.editableEntity.campaign as CampaignModel);
  }

  @tracked pathsUsed: string[] = [];

  extractPathsUsed = task({ restartable: true }, async () => {
    let sections: CreateEventComposerSection[] = [];

    if (this.recipientCondition != null) {
      sections.push(this.recipientCondition);
    }

    if (this.eventName != null) {
      sections.push(this.eventName);
    }

    if (this.bodyRows != null) {
      sections.push(...this.bodyRows.rows);
    }

    this.pathsUsed = await extractUsedPaths(this.template, sections);
  });

  @action
  previewDidChange() {
    super.previewDidChange();

    this.extractPathsUsed.perform();
  }

  private fromSidebarUpdateSectionTypeAndValue(
    section: CreateEventComposerSection,
    topLevelKey: string,
    value: string,
    targetType: DataActionConditionType,
    fallback?: string,
  ) {
    if (section.type === DataActionConditionType.Liquid) {
      let liquidVariable = `${topLevelKey}.${value}`;
      value = section.value + getLiquidToInsert(liquidVariable, fallback, this.template.templateEngine);
    } else if (section.type === DataActionConditionType.JavaScript) {
      let js = `${topLevelKey}.${value}`;
      value = fallback ? `return ${js} ? ${js} : "${fallback}";` : `return ${js};`;
    } else {
      section.type = targetType;
    }
    section.value = value;
  }

  @action
  insertSidebarValue(
    fieldInfo: [DataActionComposerSectionType | string, number | null] | null,
    value: string,
    fallback?: string,
  ) {
    // Value can be of form customer.attribute_name or customer["attribute name"]
    // If we convert it to a path, we can easily extract the top level key by
    // taking the first array item
    let path = stringToPath(value);
    let [rootKey, valueKey, ...remainingKeys] = path;

    let topLevelKey = rootKey as 'customer' | 'event' | 'trigger';

    // recreate the value string, but without the top level key
    // (customer/event/trigger), so for example converts
    // - customer.attribute_name to attribute_name
    // - customer["attribute name"] to ["attribute name"]
    // - trigger[0] to [0]
    // - trigger["0"] to ["0"]

    if (typeof valueKey === 'number') {
      value = `[${valueKey}]`;
    } else if (isNumeric(valueKey)) {
      value = `["${valueKey}"]`;
    } else {
      value = keyPathToString([valueKey]);
    }

    // If the path is more than just customer.xyz (for example,
    // trigger.data.first_name), let's recreate the value path string but
    // without the root key
    if (remainingKeys.length > 0) {
      value = keyPathToString([valueKey, ...remainingKeys]);
    }

    let targetType = getConditionTypeFromTopLevelKey(topLevelKey);

    let fieldName: string | null = null;
    let index: number | null = null;

    if (fieldInfo != null) {
      [fieldName, index] = fieldInfo;
    }
    // If no input field is focused, and the editor is "default", insert a new
    // row. For the JSON editor, don't do anything (the JSON editor is handled
    // below, which only happens if it's focused)
    if (fieldInfo == null || isEmpty(fieldName)) {
      if (this.template.editor === Editor.DEFAULT && this.bodyRows != null) {
        this.bodyRows.addRow({
          name: getRowNameFromPath(stringToPath(value)),
          type: targetType,
          value,
        });
      }
      return;
    }

    if (this.recipientCondition != null && fieldName === DataActionComposerSectionType.Recipient) {
      this.fromSidebarUpdateSectionTypeAndValue(this.recipientCondition, topLevelKey, value, targetType, fallback);
      return;
    }

    if (this.eventName != null && fieldName === DataActionComposerSectionType.EventName) {
      this.fromSidebarUpdateSectionTypeAndValue(this.eventName, topLevelKey, value, targetType, fallback);
      return;
    }

    if (this.bodyRows != null && fieldName === DataActionComposerSectionType.BodyRow && index != null) {
      let row = this.bodyRows.rows[index];
      if (isEmpty(row.name)) {
        row.name = getRowNameFromPath(stringToPath(value));
      }
      this.fromSidebarUpdateSectionTypeAndValue(row, topLevelKey, value, targetType, fallback);
      return;
    }

    if (fieldName === DataActionComposerSectionType.BodyJSON) {
      // Temporarily unsubscribe from the liquid insert event bus call, so that
      // we can publish the insert event for the Ace editor instance within the
      // composer
      this.unsubscribeFromLiquidInsert();
      this.eventBus.publish(INSERT_LIQUID, fieldInfo, `${topLevelKey}.${value}`, fallback);
      this.subscribeToLiquidInsert();
      this.eventBus.publish(FOCUS_ACE_EDITOR, fieldName);
      return;
    }
  }

  @action
  onTriggerDataSidebarRowClick(value: string, clickTarget?: DataActionComposerSectionType, targetIndex?: number) {
    let fieldInfo: [DataActionComposerSectionType, number | null] | null = null;

    if (clickTarget != null) {
      fieldInfo = [clickTarget, targetIndex ?? null];
    }

    this.insertSidebarValue(fieldInfo, `trigger.${value}`);
  }

  @tracked highlightedItem: DataActionTriggerAttributeDropdownItem | null = null;

  @action
  updateHighlight(highlighted: DataActionTriggerAttributeDropdownItem | null) {
    this.highlightedItem = highlighted;
  }
}
